import { crDateOf, Money, dec } from "@despejo/domain";
import type { IvaInput, PurchaseLine, SaleLine } from "@despejo/tax-engine";

/** Legal IVA rates (Ley 6826 arts. 10-11). */
const RATES = ["0.13", "0.04", "0.02", "0.01", "0.005"];

export interface LedgerTx {
  id: string;
  direction: "INCOME" | "EXPENSE";
  description: string | null;
  subtotal: string;
  tax_amount: string;
  total_crc: string;
  exchange_rate: string | null;
  has_electronic_receipt: boolean;
  business_use: "BUSINESS" | "PERSONAL" | "MIXED";
  business_pct: string | null;
  receipt_acceptance: string | null;
  /** Legal acceptance deadline of the linked receipt (CR date), when known. */
  receipt_acceptance_due?: string | null;
  receipt_doc_type: string | null;
  lines: LedgerLine[];
}

export interface LedgerLine {
  line_no: number;
  taxable_base: string;
  iva_rate: string | null;
  iva_amount: string;
  exemption: unknown;
  tax_category: string | null;
}

export interface MappingIssue {
  ref: string;
  message: string;
}

/** Infer the rate only when IVA / base matches a legal rate to within ₡1. */
export function inferRate(base: Money, iva: Money): string | null {
  if (iva.isZero() || base.isZero()) return null;
  for (const r of RATES) if (base.times(r).sub(iva).abs().cmp(Money.of("1")) <= 0) return r;
  return null;
}

function crc(amount: string, rate: string | null): Money {
  return rate ? Money.of(amount).times(rate) : Money.of(amount);
}

export function ledgerToIvaInput(
  period: string,
  txs: readonly LedgerTx[],
  opts: { provisionalProportionPct: string | null; cardRetentions: string; priorCreditBalance: string; /** CR date the calculation is made (acceptance windows). */ asOf?: string },
): { input: IvaInput; issues: MappingIssue[] } {
  const sales: SaleLine[] = [];
  const purchases: PurchaseLine[] = [];
  const issues: MappingIssue[] = [];
  for (const t of txs) {
    const label = t.description ?? t.id;
    if (t.direction === "INCOME") {
      if (t.lines.length) {
        for (const l of t.lines) {
          const ref = `${t.id}#${l.line_no}`;
          const base = crc(l.taxable_base, t.exchange_rate);
          const iva = crc(l.iva_amount, t.exchange_rate);
          const cat = (l.tax_category as SaleLine["category"] | null) ?? (t.receipt_doc_type === "09" ? "EXPORT" : l.exemption ? "EXEMPT" : l.iva_rate && dec(l.iva_rate).gt(0) ? "TAXED" : null);
          if (!cat) {
            issues.push({ ref, message: `${label}: línea sin tratamiento de IVA (¿exenta, no sujeta o exportación?)` });
            continue;
          }
          sales.push({ ref, category: cat, rate: cat === "TAXED" ? l.iva_rate : null, base: base.toString(), iva: iva.toString() });
        }
        continue;
      }
      const base = crc(t.subtotal, t.exchange_rate);
      const iva = crc(t.tax_amount, t.exchange_rate);
      const rate = inferRate(base, iva);
      if (rate) sales.push({ ref: t.id, category: "TAXED", rate, base: base.toString(), iva: iva.toString() });
      else issues.push({ ref: t.id, message: `${label}: no se puede determinar la tarifa ni si es exenta — clasifique el movimiento` });
      continue;
    }
    // EXPENSE
    const accepted = t.receipt_acceptance ? ["ACCEPTED", "PARTIALLY_ACCEPTED"].includes(t.receipt_acceptance) : t.has_electronic_receipt;
    const asOf = opts.asOf ?? crDateOf(new Date());
    const acceptancePendingUntil = t.receipt_acceptance === "PENDING" && t.receipt_acceptance_due && t.receipt_acceptance_due >= asOf ? t.receipt_acceptance_due : null;
    const common = {
      use: "TAXED" as PurchaseLine["use"],
      accepted,
      ...(acceptancePendingUntil ? { acceptancePendingUntil } : {}),
      hasElectronicReceipt: t.has_electronic_receipt,
      businessUse: t.business_use,
      businessPct: t.business_pct,
      servesReducedRate: null,
    };
    if (t.lines.length) {
      for (const l of t.lines) {
        const use = l.tax_category === "EXEMPT" || l.tax_category === "MIXED" ? (l.tax_category as PurchaseLine["use"]) : "TAXED";
        purchases.push({ ...common, use, ref: `${t.id}#${l.line_no}`, rate: l.iva_rate, base: crc(l.taxable_base, t.exchange_rate).toString(), iva: crc(l.iva_amount, t.exchange_rate).toString() });
      }
    } else {
      const base = crc(t.subtotal, t.exchange_rate);
      const iva = crc(t.tax_amount, t.exchange_rate);
      purchases.push({ ...common, ref: t.id, rate: inferRate(base, iva), base: base.toString(), iva: iva.toString() });
    }
  }
  return { input: { period, sales, purchases, ...opts }, issues };
}
